import { Chess } from 'chess.js';
import { SupabaseService } from './supabaseService';
import { supabase } from './supabaseClient';

export const AntiCheatService = {
    // We create a fresh worker for analysis to avoid state pollution or concurrent usage issues
    getBestMove: (fen: string): Promise<{ move: string, score: number, isMate: boolean }> => {
        return new Promise((resolve) => {
            const worker = new Worker('/stockfish.js');
            let bestMove = '';
            let score = 0;
            let isMate = false;

            worker.onmessage = (e) => {
                const msg = e.data;
                if (typeof msg === 'string') {
                    if (msg.startsWith('info') && msg.includes('score')) {
                        const parts = msg.split(' ');
                        const scoreIdx = parts.indexOf('score');
                        if (scoreIdx !== -1) {
                            const type = parts[scoreIdx + 1];
                            const val = parseInt(parts[scoreIdx + 2]);
                            if (type === 'mate') {
                                isMate = true;
                                score = val; // Moves to mate
                            } else {
                                isMate = false;
                                score = val; // Centipawns
                            }
                        }
                    }
                    if (msg.startsWith('bestmove')) {
                        bestMove = msg.split(' ')[1];
                        worker.terminate();
                        resolve({ move: bestMove, score, isMate });
                    }
                }
            };

            worker.postMessage(`position fen ${fen}`);
            worker.postMessage('go depth 12'); // Fast but deep enough for tactics
        });
    },

    analyzeGame: async (pgn: string, playerSide: 'w' | 'b', playerId: string) => {
        const game = new Chess();
        try {
            game.loadPgn(pgn);
        } catch (e) {
            console.error("AntiCheat: Invalid PGN", e);
            return;
        }

        const history = game.history({ verbose: true });
        if (history.length < 10) return; // Too short

        let suspicionScore = 0;

        // Helper: Static Material Count
        const getMaterialValue = (fen: string, side: 'w' | 'b') => {
            const pieceValues: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9 };
            let val = 0;
            const rows = fen.split(' ')[0];
            for (const char of rows) {
                if (side === 'w') {
                    if ("PNBRQ".includes(char)) val += pieceValues[char.toLowerCase()];
                } else {
                    if ("pnbrq".includes(char)) val += pieceValues[char];
                }
            }
            return val;
        };

        const replay = new Chess();
        for (const move of history) {
            const fenBefore = replay.fen();
            const turn = replay.turn(); // 'w' or 'b'

            // Reconstruct the move string needed for comparison (from-to)
            const moveLan = move.from + move.to + (move.promotion || '');

            // Execute their move
            replay.move(move.san);
            const fenAfter = replay.fen();

            if (turn === playerSide) {
                const matBefore = getMaterialValue(fenBefore, playerSide);
                const matAfter = getMaterialValue(fenAfter, playerSide);

                // Detection Logic: Mate > Material
                // If material is sacrificed (count went down)
                // But engine sees a forced mate sequence

                if (matAfter < matBefore) {
                    const analysis = await AntiCheatService.getBestMove(fenBefore);

                    if (analysis.isMate && analysis.move === moveLan) {
                        // High confidence correlation
                        suspicionScore += 1;
                        console.log("AntiCheat: Detected Mate Sacrifice pattern.", move.san);
                    }
                }
            }
        }

        console.log(`[AntiCheat] Analysis Complete. Suspicion Score: ${suspicionScore}`);

        if (suspicionScore >= 2) {
            await AntiCheatService.flagUser(playerId, suspicionScore);
        }
    },

    flagUser: async (userId: string, addedScore: number) => {
        const { data: profile } = await supabase.from('profiles').select('suspicion_score, is_banned').eq('id', userId).single();

        if (profile) {
            const newScore = (profile.suspicion_score || 0) + addedScore;
            const shouldBan = newScore >= 5;

            if (shouldBan && !profile.is_banned) {
                console.warn(`[AntiCheat] BANHAMMER: User ${userId} banned.`);
            }

            await supabase.from('profiles').update({
                suspicion_score: newScore,
                is_banned: shouldBan ? true : profile.is_banned
            }).eq('id', userId);
        }
    }
};
